/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { AllCategories, BlogPostPageProps } from 'components/FilteredBlogPosts';

import {
  formatCurrency,
  formatMaturity,
  formatPercents,
  getBalanceComputationDisplayName,
  getInterestPostedDisplayName
} from '@savebetter/api';
import { Product } from '@savebetter/components';

import 'url-search-params-polyfill';

import { ProductOfferQueryItem } from '../hooks/useProductOffer';
interface Params {
  [key: string]: string;
}

export interface SetCookieOptions {
  path?: string;
  expires?: Date;
  maxAge?: number;
  domain?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: boolean | 'none' | 'lax' | 'strict';
}

interface CookieParam {
  name: string;
  value: string;
  options?: SetCookieOptions;
}

export interface Category {
  uuid: string;
  name: string;
  slug: string;
}

export interface BlogPostData {
  node: {
    uuid: string;
    content: string;
    full_slug: string;
    created_at: string;
    published_at: string;
    first_published_at: string;
    published: boolean;
  };
}

export const stringToSlug = (str: string): string => {
  str = str.replace(/^\s+|\s+$/g, '').toLowerCase();
  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-'); // collapse dashes

  return str;
};

const paramNames = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term'];

const getUrlSearchStr = (url: string = window.location.href): string =>
  url.includes('?') ? url.split('?')[1].split('#')[0] : '';

export const getUrlParams = (url: string = window.location.href): Params =>
  getUrlSearchStr(url)
    .split('&')
    .reduce<Params>((params: Params, pair: string): Params => {
      const [key, value] = `${pair}=`.split('=').map(decodeURIComponent);

      return key.length > 0 ? { ...params, [key]: value } : params;
    }, {});

const generateExpiration = (expiresInDays = 30): Date =>
  new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * expiresInDays);

export const generateOptions = (options?: SetCookieOptions): string =>
  options instanceof Object
    ? Object.entries(options).reduce((acc: string, [key, value]: string[]): string => {
        const isBooleanValue = `${value}` === 'true' || `${value}` === 'false';
        return isBooleanValue ? (`${value}` === 'true' ? `${acc}${key};` : '') : `${acc}${key}=${value};`;
      }, '')
    : '';

export const setCookie = (name: string, value: string, options?: SetCookieOptions): string =>
  `${name}=${value};${generateOptions(options)}`;

const URLpolyfill = (url: string): { [key: string]: string } | null => {
  const match = url.match(/^(?:(https?:)\/\/)?(([^:/?#]*)(?::([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
  if (!match) {
    return {};
  }
  const [protocol, host, hostname, port, pathname, search, hash] = match;
  return {
    href: url,
    protocol,
    host,
    hostname,
    port,
    pathname,
    search,
    hash
  };
};

const getDomain = (url: string): string => {
  url = !url.includes('://') ? `https://${url}` : url;
  const urlObj = URL instanceof Object ? new URL(url) : URLpolyfill(url);
  if (!urlObj) {
    return '';
  }
  const { hostname } = urlObj;
  const hostParts = hostname.split('.');

  return hostParts.length - 1 > 1 ? `${hostParts[hostParts.length - 2]}.${hostParts[hostParts.length - 1]}` : hostname;
};

const getOptions = (hostname: string): SetCookieOptions => ({
  domain: getDomain(hostname),
  expires: generateExpiration(),
  path: '/',
  secure: true
});

export const generateCookies = (urlParams: Params, location: Location): CookieParam[] => {
  const { hostname } = location;

  return paramNames
    .filter(paramName => paramName in urlParams && urlParams[paramName].length > 0)
    .map(name => ({ name, value: urlParams[name], options: getOptions(hostname) }), []);
};

export const getSortedCategories = (
  allCategories: AllCategories,
  firstCategorySlug?: string,
  howMany = 4
): Category[] => {
  const categories = allCategories.edges.map(({ node: { slug, name, uuid } }) => ({ name, slug, uuid }));
  const firstCategoryObj = categories.find(({ slug }) => slug === firstCategorySlug);
  // promotes firstCategorySlug in position 1 if set as argument,
  // shrinks the array of categories outputed based on the number howMany
  return firstCategoryObj instanceof Object
    ? [
        firstCategoryObj,
        ...categories.filter(({ slug }) => slug !== firstCategorySlug).filter((_, index) => index < howMany)
      ]
    : categories.filter((_, index) => index <= howMany);
};

export const getTabSettings = (blogPostCategoriesCount = 0): { [key: string]: { [key: string]: number } } => ({
  lg: {
    slidesPerPage: blogPostCategoriesCount
  },
  md: {
    slidesPerPage: blogPostCategoriesCount
  },
  sm: {
    slidesPerPage: Math.round(blogPostCategoriesCount / 2)
  }
});

export const getCategoryPostsCountObject = (
  blogPostCategories: Category[],
  blogPosts: BlogPostPageProps[]
): { [key: string]: number } => {
  const counterObj: { [key: string]: number } = {};
  // creates a an object that maps published categories and calculates the number of posts for each category
  blogPostCategories.forEach(({ slug, uuid }) => {
    if (!(slug in counterObj)) {
      counterObj[slug] = blogPosts.filter(({ categories }) => categories?.includes(uuid)).length || 0;
    }
  });

  return counterObj;
};

export const getBlogPosts = (blogPostsData: BlogPostData[]): BlogPostPageProps[] =>
  blogPostsData.map(({ node: { content, created_at, published_at, first_published_at, full_slug } }) => {
    const parsedContent = JSON.parse(content);

    return { ...parsedContent, created_at, published_at, first_published_at, full_slug };
  });

export const filterBlogPosts = (
  blogPosts: BlogPostPageProps[],
  activeCategoryTab: Category,
  displayedCategoryArticles: number,
  sortField: 'created_at' | 'published_at' | 'first_published_at',
  sortDirection: 'asc' | 'desc'
): BlogPostPageProps[] =>
  blogPosts
    .filter(({ categories }) => categories && categories.includes(activeCategoryTab.uuid))
    .sort((a: { [key: string]: any }, b: { [key: string]: any }) => {
      const aDate = Number(new Date(a[sortField]));
      const bDate = Number(new Date(b[sortField]));

      return sortDirection === 'desc' ? aDate - bDate : bDate - aDate;
    })
    .filter((_, index) => index < displayedCategoryArticles);

// prepares product cards data to be consumed by a component
export const getPreparedProducts = (products: ProductOfferQueryItem[], depositAmount?: number): Product[] =>
  products.map(({ id, productBank, name, type, maturity, apy, minAmount, balanceComputation, interestPosted }) => ({
    id,
    bankId: productBank.id,
    bankName: productBank.name,
    bankLogoUrl: productBank.logoUrl || '',
    productType: type,
    productName: name,
    maturity: maturity && type !== 'SAVINGS_ACCOUNT' ? formatMaturity(maturity) : ' ',
    annualPercentageYield: formatPercents(apy / 100, { maximumFractionDigits: 2 }),
    minOpeningDeposit: formatCurrency(minAmount, { currency: 'USD', minimumFractionDigits: 0 }),
    interestCompound: getBalanceComputationDisplayName(balanceComputation),
    interestCredited: getInterestPostedDisplayName(interestPosted),
    annualEarnings:
      (depositAmount && formatCurrency(depositAmount * (apy / 100), { currency: 'USD', minimumFractionDigits: 2 })) ||
      undefined
  }));

export const getCrumbs = (crumbs?: Location[]): string[] => {
  const crumbNames =
    crumbs && crumbs.length > 0
      ? `${crumbs[crumbs.length - 1].pathname}${crumbs[crumbs.length - 1].search}`
          .split('/')
          .filter(path => path !== '')
      : [''];
  return ['home', ...crumbNames];
};

// retrieves crumb if crumb has params
const getCrumbFromParams = (crumb: string): string => {
  const params = new URLSearchParams(crumb.split('?')[1]);
  return params.get('path') || '';
};

// retrieves crumb name to display
export const getCrumbName = (relativeHref = '/'): string => {
  if (relativeHref === '/') {
    return 'home';
  }
  const crumbs = relativeHref.split('/');
  let lastCrumb = crumbs[crumbs.length - 1];
  // when url contains editor, we select path param as last crumb
  if (lastCrumb.includes('?')) {
    lastCrumb = getCrumbFromParams(lastCrumb);
  }
  if (lastCrumb.includes('&')) {
    lastCrumb = lastCrumb.split('&')[0];
  }
  return lastCrumb;
};

// builds breadcrumbs url title to display
export const getCrumbLabel = (crumb = 'home'): string => {
  // when url contains editor, we select path param as last crumb
  if (crumb === 'undefined') {
    crumb = '';
  }
  if (crumb.includes('?')) {
    crumb = getCrumbFromParams(crumb);
  }
  if (crumb.includes('&')) {
    crumb = crumb.split('&')[0];
  }
  // convers all-blogs in blogs for Breadcrumbs consistency
  if (crumb === 'all-blogs') {
    crumb = 'blogs';
  }

  // kebab case (my-title) to title case (My Title)
  return crumb
    .toLowerCase()
    .split('-')
    .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};

// Important: blogs prop is an override for new-blog-homepage.
// It should be removed in case Storyblok members rename new-blog-homepage into blogs
const exceptionCrumbs: { [key: string]: string } = {
  home: '',
  blogs: 'education-center'
};

// builds breadcrumb URL
export const getCrumbHref = (crumb: string): string => {
  // resolves path in preview
  if (crumb.includes('blogPostPage')) {
    return `/blogs/${crumb.split('&')[0]}`;
  }
  // resolves path in storyblok editor
  if (crumb.includes('path=blogs')) {
    return exceptionCrumbs['all-blogs'];
  }
  // resolves path in localhost and prod
  return `/${crumb in exceptionCrumbs ? exceptionCrumbs[crumb] : crumb}`;
};

export const getCookieByName = (cookieName: string) => {
  const decodedCookie = decodeURIComponent(typeof window !== 'undefined' ? document.cookie : '');

  const cookieArray = decodedCookie.split(';');

  for (let i = 0; i < cookieArray.length; i++) {
    const cookie = cookieArray[i];

    const item = cookie.split('=');
    const storedKey = item[0].trim();
    const storedValue = item[1];

    if (storedKey === cookieName) {
      return storedValue;
    }
  }

  return '';
};

export const deleteCookie = (cookieName: string) => {
  document.cookie = setCookie(cookieName, '');
};
